import React, { useState, useEffect, useCallback } from 'react';
import { apiService, Product } from '../services/api';
import { Plus, Edit, Trash2, Package, Search, Star } from 'lucide-react';
import { useToast } from '../contexts/ToastContext';
import { getImageUrl } from '../utils/imageUtils';

const ProductsPage: React.FC = () => {
  const { showToast } = useToast();
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);
  const [searchTerm, setSearchTerm] = useState('');

  const fetchProducts = useCallback(async () => {
    try {
      setLoading(true);
      const data = await apiService.getProducts();
      setProducts(data || []);
    } catch (error) {
      console.error('Failed to fetch products:', error);
      showToast('Failed to fetch products.', 'error');
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  const handleCloseModal = () => {
    setShowForm(false);
    setEditingProduct(null);
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const fd = new FormData(e.currentTarget);

    // Normalize booleans
    ['is_featured', 'is_active'].forEach(field => {
      if (fd.has(field)) {
        const val = fd.get(field);
        fd.set(field, val === 'on' || val === 'true' ? 'true' : 'false');
      } else {
        fd.append(field, 'false');
      }
    });

    // Handle schema_markup as JSON string
    const schemaMarkup = fd.get('schema_markup');
    if (schemaMarkup && typeof schemaMarkup === 'string' && schemaMarkup.trim()) {
      try {
        JSON.parse(schemaMarkup);
      } catch {
        showToast('Invalid JSON in Schema Markup field', 'error');
        return;
      }
    }

    try {
      if (editingProduct) {
        await apiService.updateProduct(editingProduct.slug, fd);
        showToast(`Product updated successfully!`, 'success');
      } else {
        await apiService.createProduct(fd);
        showToast(`Product created successfully!`, 'success');
      }
      handleCloseModal();
      fetchProducts();
    } catch (error) {
      console.error('Failed to save product:', error);
      showToast('Error saving product.', 'error');
    }
  };

  const handleDelete = async (slug: string) => {
    if (!window.confirm('Are you sure you want to delete this product?')) return;
    try {
      await apiService.deleteProduct(slug);
      showToast('Product deleted successfully!', 'success');
      fetchProducts();
    } catch (error) {
      console.error('Failed to delete product:', error);
      showToast('Error deleting product.', 'error');
    }
  };

  const filteredProducts = products.filter(product =>
    product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    product.brand?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-600"></div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 max-w-7xl mx-auto">
      <div className="mb-8 flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Products</h1>
          <p className="text-gray-600">Manage your electrical equipment catalog</p>
        </div>
        <button
          onClick={() => { setEditingProduct(null); setShowForm(true); }}
          className="inline-flex items-center px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition"
        >
          <Plus className="h-4 w-4 mr-2" />
          Add Product
        </button>
      </div>

      <div className="mb-6">
        <div className="relative">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
          <input
            type="text"
            placeholder="Search products by name or brand..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-12 pr-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-orange-500/20 outline-none"
          />
        </div>
      </div>

      {showForm && (
        <div className="fixed inset-0 bg-gray-900/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
            <div className="p-6 border-b border-gray-100 flex justify-between items-center bg-gray-50">
              <h3 className="text-xl font-bold text-gray-900">
                {editingProduct ? 'Edit Product' : 'New Product'}
              </h3>
              <button onClick={() => setShowForm(false)} className="text-gray-400 hover:text-gray-600 text-2xl">&times;</button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-4 overflow-y-auto">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Product Name</label>
                  <input name="name" defaultValue={editingProduct?.name} required className="w-full border border-gray-200 rounded-lg px-4 py-2" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Brand</label>
                  <input name="brand" defaultValue={editingProduct?.brand} required className="w-full border border-gray-200 rounded-lg px-4 py-2" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Model Number</label>
                  <input name="model_number" defaultValue={editingProduct?.model_number} className="w-full border border-gray-200 rounded-lg px-4 py-2" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Price</label>
                  <input type="number" step="0.01" name="price" defaultValue={editingProduct?.price} className="w-full border border-gray-200 rounded-lg px-4 py-2" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Featured Image</label>
                  <input type="file" name="featured_image" accept="image/*" required={!editingProduct} className="block w-full text-xs text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:bg-gray-100 file:text-gray-700" />
                  {editingProduct?.featured_image && (
                    <div className="mt-2 h-20 w-32 rounded-lg overflow-hidden bg-gray-100 border border-gray-200">
                      <img src={getImageUrl(editingProduct.featured_image) || undefined} alt="" className="h-full w-full object-cover" />
                    </div>
                  )}
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Short Description</label>
                  <textarea name="short_description" defaultValue={editingProduct?.short_description} required rows={2} className="w-full border border-gray-200 rounded-lg px-4 py-2" />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Full Description</label>
                  <textarea name="description" defaultValue={editingProduct?.description} required rows={3} className="w-full border border-gray-200 rounded-lg px-4 py-2" />
                </div>

                <div className="md:col-span-2 border-t pt-4">
                  <h4 className="font-bold text-gray-900 mb-3">SEO Settings</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-medium text-gray-500 mb-1">Featured Image Alt</label>
                      <input name="featured_image_alt" defaultValue={editingProduct?.featured_image_alt} className="w-full border border-gray-200 rounded-lg px-4 py-2 text-sm" />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-500 mb-1">Robots Meta</label>
                      <input name="robots_meta" defaultValue={editingProduct?.robots_meta} placeholder="index, follow" className="w-full border border-gray-200 rounded-lg px-4 py-2 text-sm" />
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-xs font-medium text-gray-500 mb-1">Schema Markup (JSON)</label>
                      <textarea name="schema_markup" defaultValue={editingProduct?.schema_markup ? JSON.stringify(editingProduct.schema_markup, null, 2) : ''} rows={3} placeholder='{"@type": "Product", ...}' className="w-full border border-gray-200 rounded-lg px-4 py-2 text-sm font-mono" />
                    </div>
                  </div>
                </div>

                <div className="md:col-span-2 flex items-center gap-6 pt-2">
                  <div className="flex items-center gap-2">
                    <input type="checkbox" name="is_featured" id="p_featured" defaultChecked={editingProduct?.is_featured} className="h-5 w-5 text-orange-600 rounded" />
                    <label htmlFor="p_featured" className="text-sm font-medium text-gray-700">Featured</label>
                  </div>
                  <div className="flex items-center gap-2">
                    <input type="checkbox" name="is_active" id="p_active" defaultChecked={editingProduct?.is_active !== false} className="h-5 w-5 text-orange-600 rounded" />
                    <label htmlFor="p_active" className="text-sm font-medium text-gray-700">Active</label>
                  </div>
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-6 border-t border-gray-50">
                <button type="button" onClick={() => setShowForm(false)} className="px-4 py-2 text-gray-600 font-medium">Cancel</button>
                <button type="submit" className="px-8 py-2 bg-orange-600 text-white rounded-lg font-bold hover:bg-orange-700 transition">
                  {editingProduct ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
        {filteredProducts.map((product) => (
          <div key={product.id} className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden hover:shadow-md transition group">
            <div className="relative h-48 bg-gray-50 flex items-center justify-center p-4">
              {product.featured_image ? (
                <img src={getImageUrl(product.featured_image) || undefined} alt={product.name} className="max-h-full max-w-full object-contain" />
              ) : (
                <Package className="h-12 w-12 text-gray-200" />
              )}
              <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition">
                <button
                  onClick={async () => {
                    try {
                      const detail = await apiService.getProductDetail(product.slug);
                      setEditingProduct(detail);
                      setShowForm(true);
                    } catch (err) {
                      console.error('Failed to fetch product detail', err);
                    }
                  }}
                  className="p-2 bg-white rounded shadow text-orange-600 hover:bg-orange-50"
                >
                  <Edit className="h-4 w-4" />
                </button>
                <button onClick={() => handleDelete(product.slug)} className="p-2 bg-white rounded shadow text-red-600 hover:bg-red-50">
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </div>
            <div className="p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-[10px] font-bold text-gray-400 uppercase">{product.brand}</span>
                {product.is_featured && <Star className="h-4 w-4 text-yellow-500 fill-current" />}
              </div>
              <h3 className="font-bold text-gray-900 truncate mb-1">{product.name}</h3>
              <p className="text-xs text-gray-500 line-clamp-2 mb-3">{product.short_description}</p>
              <div className="flex items-center justify-between pt-3 border-t border-gray-50">
                <span className="text-sm font-bold text-orange-600">
                  NRS {product.price ? Number(product.price).toFixed(2) : 'N/A'}
                </span>
                <span className="text-[10px] text-gray-400 font-bold">{product.model_number || 'No Model'}</span>
              </div>
            </div>
          </div>
        ))}
      </div>

      {filteredProducts.length === 0 && (
        <div className="text-center py-24 bg-gray-50 rounded-xl border border-gray-100">
          <Package className="mx-auto h-10 w-10 text-gray-300 mb-3" />
          <p className="text-gray-500">{searchTerm ? 'No products match your search.' : 'No products found.'}</p>
        </div>
      )}
    </div>
  );
};

export default ProductsPage;